/**
 * Mercato de l'équipe (espace compétition) — candidatures reçues, invitations
 * envoyées.
 *
 * Par défaut, seules les entrées des 3 derniers mois sont affichées (les
 * demandes "en attente" restent toujours visibles, quel que soit leur âge,
 * car elles peuvent nécessiter une action) ; un bouton permet de voir
 * l'historique complet.
 */

import { useState, useEffect, useCallback } from 'react';
import { PlayerAvatar } from '../../components/PlayerAvatar';
import { useProfile } from '../../contexts/ProfileContext';
import InviteMercatoModal from '../../components/competitions/InviteMercatoModal';
import { getMercato, createMercato, mercatoAction, type MyTeam, type MercatoEntry } from '../../lib/teamsApi';
import { PersonAddIcon, HourglassTableIcon } from '../../components/Icons';
import type { Session } from '../../types/app';

const THREE_MONTHS_MS = 90 * 24 * 3600 * 1000;

const STATE_LABEL: Record<string, string> = {
  attente: 'En attente', ok: 'Acceptée', refus: 'Refusée', abandon: 'Annulée',
};

function fmtRelative(v: string): string {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  const days = Math.floor((Date.now() - d.getTime()) / (24 * 3600 * 1000));
  if (days <= 0) return "aujourd'hui";
  if (days === 1) return 'hier';
  if (days < 30) return `il y a ${days} j`;
  const months = Math.floor(days / 30);
  if (months < 12) return `il y a ${months} mois`;
  return d.toLocaleDateString('fr-FR');
}

interface TeamMercatoViewProps {
  session: Session;
  team: MyTeam;
}

export default function TeamMercatoView({ session, team }: TeamMercatoViewProps) {
  const token = session.token!;
  const [mercato, setMercatoItems] = useState<MercatoEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showFullHistory, setShowFullHistory] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [subtab, setSubtab] = useState<'received' | 'sent'>('received');

  const load = useCallback(() => {
    setLoading(true);
    getMercato(token, team.id)
      .then((d) => setMercatoItems(d.items))
      .catch(() => setMercatoItems([]))
      .finally(() => setLoading(false));
  }, [token, team.id]);

  useEffect(() => { load(); }, [load]);

  const run = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      load();
    } catch {
      setError('Action impossible');
    } finally {
      setBusy(false);
    }
  };

  const cutoff = Date.now() - THREE_MONTHS_MS;
  const visible = (entries: MercatoEntry[]) => showFullHistory
    ? entries
    : entries.filter((m) => m.etat === 'attente' || new Date(m.date).getTime() >= cutoff);

  const received = visible(mercato.filter((m) => m.initiative === 'joueur'));
  const sent = visible(mercato.filter((m) => m.initiative === 'equipe'));
  const hiddenCount = mercato.length - visible(mercato).length;

  const pendingReceived = mercato.filter((m) => m.initiative === 'joueur' && m.etat === 'attente').length;

  return (
    <div className="tm-detail">
      {error && <div className="compet-error">{error}</div>}

      <section>
        <div className="tm-section-header">
          <h3>Mercato</h3>
          {team.is_staff && (
            <button className="cia-btn cia-btn--primary cia-btn--with-icon" onClick={() => setShowInvite(true)}>
              <PersonAddIcon size={15} /> Inviter un joueur
            </button>
          )}
        </div>

        <div className="tm-subtabs">
          <button className={`tm-subtab ${subtab === 'received' ? 'active' : ''}`} onClick={() => setSubtab('received')}>
            Candidatures reçues{pendingReceived > 0 ? ` (${pendingReceived})` : ''}
          </button>
          <button className={`tm-subtab ${subtab === 'sent' ? 'active' : ''}`} onClick={() => setSubtab('sent')}>
            Invitations envoyées
          </button>
        </div>

        {loading ? (
          <div className="compet-loading" style={{ marginTop: 14 }}>Chargement…</div>
        ) : (
          <div style={{ marginTop: 14 }}>
            {subtab === 'received' && (
              received.length === 0
                ? <div className="compet-empty">Aucune candidature{showFullHistory ? '' : ' récente'}</div>
                : received.map((m) => (
                  <MercatoRow
                    key={m.id}
                    entry={m}
                    busy={busy}
                    canAccept={team.is_staff && m.etat === 'attente'}
                    canCancel={false}
                    onAccept={() => run(async () => { await mercatoAction(token, team.id, m.id, 'accept'); })}
                    onReject={() => run(async () => { await mercatoAction(token, team.id, m.id, 'reject'); })}
                  />
                ))
            )}
            {subtab === 'sent' && (
              sent.length === 0
                ? <div className="compet-empty">Aucune invitation{showFullHistory ? '' : ' récente'}</div>
                : sent.map((m) => (
                  <MercatoRow
                    key={m.id}
                    entry={m}
                    busy={busy}
                    canAccept={false}
                    canCancel={team.is_staff && m.etat === 'attente'}
                    onCancel={() => run(async () => { await mercatoAction(token, team.id, m.id, 'cancel'); })}
                  />
                ))
            )}

            {!showFullHistory && hiddenCount > 0 && (
              <button className="cia-btn cia-btn--ghost cia-btn--with-icon" style={{ marginTop: 8 }} onClick={() => setShowFullHistory(true)}>
                <HourglassTableIcon size={14} /> Voir l'historique complet ({hiddenCount} de plus de 3 mois)
              </button>
            )}
            {showFullHistory && (
              <button className="cia-btn cia-btn--ghost" style={{ marginTop: 8 }} onClick={() => setShowFullHistory(false)}>
                Revenir aux 3 derniers mois
              </button>
            )}
          </div>
        )}
      </section>

      {showInvite && (
        <InviteMercatoModal
          excludePseudos={team.members.map((m) => m.pseudo)}
          onClose={() => setShowInvite(false)}
          onSubmit={async (pseudo, message) => {
            await createMercato(token, team.id, 'equipe', pseudo, message || undefined);
            load();
          }}
        />
      )}
    </div>
  );
}

function MercatoRow({ entry, busy, canAccept, canCancel, onAccept, onReject, onCancel }: {
  entry: MercatoEntry;
  busy: boolean;
  canAccept: boolean;
  canCancel: boolean;
  onAccept?: () => void;
  onReject?: () => void;
  onCancel?: () => void;
}) {
  const { openPlayerProfile } = useProfile();
  const pillClass = `tm-state-pill tm-state-pill--${entry.etat}`;
  return (
    <div className="tm-mercato-card">
      <PlayerAvatar pseudo={entry.joueur} size={36} />
      <div className="tm-mercato-info">
        <button className="tm-mercato-name" onClick={() => openPlayerProfile(entry.joueur)}>
          {entry.joueur_display}
        </button>
        <div className="tm-mercato-meta">
          <span className={pillClass}>{STATE_LABEL[entry.etat] || entry.etat}</span>
          <span>{fmtRelative(entry.date)}</span>
        </div>
        {entry.message && <span className="tm-mercato-message">« {entry.message} »</span>}
      </div>
      <div className="tm-mercato-actions">
        {canAccept && (
          <>
            <button className="cia-btn cia-btn--small cia-btn--primary" disabled={busy} onClick={onAccept}>Accepter</button>
            <button className="cia-btn cia-btn--small" disabled={busy} onClick={onReject}>Refuser</button>
          </>
        )}
        {canCancel && <button className="cia-btn cia-btn--small cia-btn--danger" disabled={busy} onClick={onCancel}>Annuler</button>}
      </div>
    </div>
  );
}
